/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module Angular-Core
 */

/**
 * @fileoverview Angular components need to be specified here to be downgraded and used in
 *     AngularJS.
 */
import angular from 'angular';
import { downgradeComponent } from '@angular/upgrade/static';
import { BgpPeersPageComponent }
    from 'ng/modules/service-engine/components/bgp-peers-page/bgp-peers-page.component';

import {
    AviNotificationComponent,
    AviNotificationsPortalComponent,
    AviToastComponent,
    UtcMismatchNotificationComponent,
} from './angular/modules/notification';

import {
    AviConfirmationComponent,
    AviDialogsPortalComponent,
} from './angular/modules/dialog';

import {
    AviAlertGroupComponent,
} from './angular/shared/components/avi-alert-group/avi-alert-group.component';

import {
    AviCinematicsPortalComponent,
} from './angular/modules/cinematic/components/avi-cinematics-portal';

import {
    AviDropdownButtonComponent,
    AviStatePageComponent,
    CopyrightComponent,
    ExpanderComponent,
    ExpanderWithActionsComponent,
    FullModalComponent,
    GradientLoaderComponent,
    TriSwitchComponent,
} from './angular/shared/components';

import { AviDropdownComponent } from './angular/standalone-components/avi-dropdown';

import {
    AviLabelWithTooltipComponent,
} from './angular/modules/tooltip/components';

import { WafUserOverridesBadgeComponent } from './angular/modules/waf/components';

import { PolicyRuleListComponent } from './angular/modules/policy';

import {
    RecentUpdatesListComponent,
    SystemAboutCardComponent,
    UpgradeTranscriptModalComponent,
} from './angular/modules/update';

import { AviMainHeaderComponent } from './angular/modules/header';

import {
    ConfiguredNetworkSubnetExpanderComponent,
    NetworkListExpanderComponent,
} from './angular/modules/network';

import { BotDetectionResultMatchGridComponent } from './angular/modules/bot';
import { RbacLabelGridAjsWrapperComponent } from './angular/modules/rbac';

import {
    AviAppLevelAlertsPortalComponent,
    TransitionProgressBarComponent,
} from './angular/modules/core/components';

import { VsLogsPageComponent } from './angular/modules/vs-logs';

import {
    VirtualHostGridComponent,
    VirtualServiceFaultsComponent,
} from './angular/modules/virtual-service/components';

import {
    VsSnatIpAddressesComponent,
// eslint-disable-next-line max-len
} from './angular/modules/virtual-service/components/vs-snat-ip-addresses/vs-snat-ip-addresses.component';

import {
    AdministrationVerticalNavComponent,
    ApplicationsVerticalNavComponent,
    AviSkipLinkComponent,
    GslbServiceSubheaderComponent,
    InfrastructureVerticalNavComponent,
    OperationsVerticalNavComponent,
    PoolSubheaderComponent,
    TemplatesVerticalNavComponent,
    VirtualServiceSubheaderComponent,
} from './angular/modules/navigation/components';

import {
    AviMetricsCollectionDataGridComponent,
// eslint-disable-next-line max-len
} from './angular/modules/data-grid/components/avi-metrics-collection-data-grid/avi-metrics-collection-data-grid.component';

/**
 * List out Angular components to downgrade here. Names must be listed explicitly and not generated
 * automatically from the component class since that information gets stripped in the dist build.
 */
const downgradeComponents = [
    {
        ajsName: 'aviConfirmation',
        component: AviConfirmationComponent,
    },
    {
        ajsName: 'aviAlertGroup',
        component: AviAlertGroupComponent,
    },
    {
        ajsName: 'expander',
        component: ExpanderComponent,
    },
    {
        ajsName: 'expanderWithActions',
        component: ExpanderWithActionsComponent,
    },
    {
        ajsName: 'gradientLoader',
        component: GradientLoaderComponent,
    },
    {
        ajsName: 'recentUpdatesList',
        component: RecentUpdatesListComponent,
    },
    {
        ajsName: 'utcMismatchNotification',
        component: UtcMismatchNotificationComponent,
    },
    {
        ajsName: 'aviNotification',
        component: AviNotificationComponent,
    },
    {
        ajsName: 'aviToast',
        component: AviToastComponent,
    },
    {
        ajsName: 'aviStatePage',
        component: AviStatePageComponent,
    },
    {
        ajsName: 'aviLabelWithTooltip',
        component: AviLabelWithTooltipComponent,
    },
    {
        ajsName: 'policyRuleList',
        component: PolicyRuleListComponent,
    },
    {
        ajsName: 'triSwitch',
        component: TriSwitchComponent,
    },
    {
        ajsName: 'aviNotificationsPortal',
        component: AviNotificationsPortalComponent,
    },
    {
        ajsName: 'aviDropdown',
        component: AviDropdownComponent,
    },
    {
        ajsName: 'fullModal',
        component: FullModalComponent,
    },
    {
        ajsName: 'aviDropdownButton',
        component: AviDropdownButtonComponent,
    },
    {
        ajsName: 'copyright',
        component: CopyrightComponent,
    },
    {
        ajsName: 'networkListExpander',
        component: NetworkListExpanderComponent,
    },
    {
        ajsName: 'configuredNetworkSubnetExpander',
        component: ConfiguredNetworkSubnetExpanderComponent,
    },
    {
        ajsName: 'rbacLabelGridAjsWrapper',
        component: RbacLabelGridAjsWrapperComponent,
    },
    {
        ajsName: 'upgradeTranscriptModal',
        component: UpgradeTranscriptModalComponent,
    },
    {
        ajsName: 'aviDialogsPortal',
        component: AviDialogsPortalComponent,
    },
    {
        ajsName: 'aviCinematicsPortal',
        component: AviCinematicsPortalComponent,
    },
    {
        ajsName: 'systemAboutCard',
        component: SystemAboutCardComponent,
    },
    {
        ajsName: 'wafUserOverridesBadge',
        component: WafUserOverridesBadgeComponent,
    },
    {
        ajsName: 'botDetectionResultMatchGrid',
        component: BotDetectionResultMatchGridComponent,
    },
    {
        ajsName: 'transitionProgressBar',
        component: TransitionProgressBarComponent,
    },
    {
        ajsName: 'aviAppLevelAlertsPortal',
        component: AviAppLevelAlertsPortalComponent,
    },
    {
        ajsName: 'bgpPeersPage',
        component: BgpPeersPageComponent,
    },
    {
        ajsName: 'ngVsLogsPage',
        component: VsLogsPageComponent,
    },
    {
        ajsName: 'virtualHostGrid',
        component: VirtualHostGridComponent,
    },
    {
        ajsName: 'virtualServiceFaults',
        component: VirtualServiceFaultsComponent,
    },
    {
        ajsName: 'vsSnatIpAddresses',
        component: VsSnatIpAddressesComponent,
    },
    {
        ajsName: 'applicationsVerticalNav',
        component: ApplicationsVerticalNavComponent,
    },
    {
        ajsName: 'operationsVerticalNav',
        component: OperationsVerticalNavComponent,
    },
    {
        ajsName: 'templatesVerticalNav',
        component: TemplatesVerticalNavComponent,
    },
    {
        ajsName: 'infrastructureVerticalNav',
        component: InfrastructureVerticalNavComponent,
    },
    {
        ajsName: 'administrationVerticalNav',
        component: AdministrationVerticalNavComponent,
    },
    {
        ajsName: 'gslbServiceSubheader',
        component: GslbServiceSubheaderComponent,
    },
    {
        ajsName: 'poolSubheader',
        component: PoolSubheaderComponent,
    },
    {
        ajsName: 'aviMetricsCollectionDataGrid',
        component: AviMetricsCollectionDataGridComponent,
    },
    {
        ajsName: 'virtualServiceSubheader',
        component: VirtualServiceSubheaderComponent,
    },
    {
        ajsName: 'aviMainHeader',
        component: AviMainHeaderComponent,
    },
    {
        ajsName: 'aviSkipLink',
        component: AviSkipLinkComponent,
    },
];

downgradeComponents.forEach(componentData => {
    const { ajsName, component } = componentData;

    if (!ajsName) {
        throw new Error(`Angular JS name needed for ${component}`);
    }

    angular.module('aviApp').directive(ajsName, downgradeComponent({ component }));
});
